import { lazy } from 'react';
import type { RouteConfigExtended } from '@lingoda/router';
import {
    RouteAccess,
    teacherBookingPaginationPath,
    teacherBookingPath,
    teacherClassMaterialsPdfPath,
    teacherClassPath,
    teacherCoursePath,
    teacherForgotPasswordPath,
    teacherForgotPasswordSuccessPath,
    teacherLearningMaterialsPath,
    teacherLoginPath,
    teacherLogoutPath,
    teacherMySchedulePaginationPath,
    teacherMySchedulePath,
    teacherPaymentsPath,
    teacherProfileNewPath,
    teacherResetPasswordPath,
    teacherServerErrorPath,
    teacherTermsPath,
    teacherViewTermsPath,
} from '@lingoda/router';
import { NotFoundPage } from '@lingoda/fallbacks';

const Main = lazy(() => import('./root/Main'));
const TeacherClass = lazy(() =>
    import('./pages/TeacherClassPage').then(({ TeacherClassPage }) => ({
        default: TeacherClassPage,
    })),
);
const TeacherClassMaterialsPdf = lazy(() =>
    import('./pages/TeacherClassMaterialsPdfPage').then(({ TeacherClassMaterialsPdfPage }) => ({
        default: TeacherClassMaterialsPdfPage,
    })),
);
const TeacherBookingPage = lazy(() =>
    import('./pages/TeacherBookingPage').then(({ TeacherBookingPage }) => ({
        default: TeacherBookingPage,
    })),
);
const TeacherMySchedulePage = lazy(() =>
    import('./pages/TeacherMySchedulePage').then(({ TeacherMySchedulePage }) => ({
        default: TeacherMySchedulePage,
    })),
);
const TeacherCoursePage = lazy(() =>
    import('./pages/TeacherCoursePage').then(({ TeacherCoursePage }) => ({
        default: TeacherCoursePage,
    })),
);
const LearningMaterials = lazy(() =>
    import('./pages/TeacherLearningMaterialsPage').then(({ TeacherLearningMaterialsPage }) => ({
        default: TeacherLearningMaterialsPage,
    })),
);
const ClassPayments = lazy(() =>
    import('./pages/TeacherClassPaymentsPage').then(({ TeacherClassPaymentsPage }) => ({
        default: TeacherClassPaymentsPage,
    })),
);
const ProfilePage = lazy(() =>
    import('./pages/TeacherProfilePage').then(({ ProfilePage }) => ({
        default: ProfilePage,
    })),
);
const AgreementsAccept = lazy(() =>
    import('@lingoda/teacher-agreements').then(({ AgreementsAccept }) => ({
        default: AgreementsAccept,
    })),
);
const AgreementsView = lazy(() =>
    import('@lingoda/teacher-agreements').then(({ AgreementsView }) => ({
        default: AgreementsView,
    })),
);

const TeacherLogout = lazy(() =>
    import('./pages/TeacherLogoutPage').then(({ TeacherLogoutPage }) => ({
        default: TeacherLogoutPage,
    })),
);

const TeacherLogin = lazy(() =>
    import('./pages/TeacherLoginPage').then(({ TeacherLoginPage }) => ({
        default: TeacherLoginPage,
    })),
);
const TeacherForgotPassword = lazy(() =>
    import('./pages/TeacherForgotPasswordPage').then(({ TeacherForgotPasswordPage }) => ({
        default: TeacherForgotPasswordPage,
    })),
);
const TeacherForgotPasswordSuccess = lazy(() =>
    import('./pages/TeacherForgotPasswordSuccessPage').then(
        ({ TeacherForgotPasswordSuccessPage }) => ({
            default: TeacherForgotPasswordSuccessPage,
        }),
    ),
);
const TeacherResetPassword = lazy(() =>
    import('./pages/TeacherResetPasswordPage').then(({ TeacherResetPasswordPage }) => ({
        default: TeacherResetPasswordPage,
    })),
);

const ServerErrorPage = lazy(() => import('./pages/ServerErrorPage'));

const routes: RouteConfigExtended[] = [
    {
        component: Main,
        label: 'Main',
        config: undefined,
        routes: [
            // Before `TeacherClass`, which isn't exact and would match it too
            {
                exact: true,
                component: TeacherClassMaterialsPdf,
                path: teacherClassMaterialsPdfPath.raw,
                label: 'Teacher Class Materials Pdf',
                config: {
                    // No login redirect: the page itself shows when the user isn't authorised
                    access: RouteAccess.All,
                },
            },
            {
                component: TeacherClass,
                path: teacherClassPath.raw,
                label: 'Teacher Class',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: TeacherBookingPage,
                path: teacherBookingPaginationPath.raw,
                label: 'Teacher Booking',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: TeacherBookingPage,
                path: teacherBookingPath.raw,
                label: 'Teacher Booking',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: TeacherMySchedulePage,
                path: teacherMySchedulePaginationPath.raw,
                label: 'Teacher Schedule',
                config: {
                    access: RouteAccess.Private,
                    tab: 'mySchedule',
                },
            },
            {
                component: TeacherMySchedulePage,
                path: teacherMySchedulePath.raw,
                label: 'Teacher Schedule',
                config: {
                    access: RouteAccess.Private,
                    tab: 'mySchedule',
                },
            },
            {
                component: TeacherCoursePage,
                path: teacherCoursePath.raw,
                label: 'Teacher Course',
                config: {
                    access: RouteAccess.Private,
                    tab: 'mySchedule',
                },
            },
            {
                component: ClassPayments,
                path: teacherPaymentsPath.raw,
                label: 'Teacher Payments',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: LearningMaterials,
                path: teacherLearningMaterialsPath.raw,
                label: 'Teacher Learning Materials',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: ProfilePage,
                path: teacherProfileNewPath.raw,
                label: 'Teacher Profile',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: AgreementsAccept,
                path: teacherTermsPath.raw,
                label: 'Teacher Terms',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: AgreementsView,
                path: teacherViewTermsPath.raw,
                label: 'Teacher View Terms',
                config: {
                    access: RouteAccess.Private,
                    tab: 'bookClasses',
                },
            },
            {
                component: TeacherLogout,
                path: teacherLogoutPath.raw,
                label: 'Teacher Logout',
                config: {
                    access: RouteAccess.All,
                },
            },
            {
                component: TeacherLogin,
                path: teacherLoginPath.raw,
                label: 'Teacher Login',
                config: {
                    access: RouteAccess.Public,
                },
            },
            {
                component: TeacherForgotPasswordSuccess,
                path: teacherForgotPasswordSuccessPath.raw,
                label: 'Teacher Forgot Password Success',
                config: {
                    access: RouteAccess.Public,
                },
            },
            {
                component: TeacherForgotPassword,
                path: teacherForgotPasswordPath.raw,
                label: 'Teacher Forgot Password',
                config: {
                    access: RouteAccess.Public,
                },
            },
            {
                component: TeacherResetPassword,
                path: teacherResetPasswordPath.raw,
                label: 'Teacher Reset Password',
                config: {
                    access: RouteAccess.Public,
                },
            },
            {
                exact: true,
                component: ServerErrorPage,
                path: teacherServerErrorPath.raw,
                label: 'ServerError',
                config: {
                    access: RouteAccess.All,
                },
            },
            {
                component: NotFoundPage,
                label: 'PageNotFound',
                config: {
                    access: RouteAccess.All,
                },
            },
        ],
    },
];

export default routes;
